<template>
  <loading-wrapper :is-loading="isLoading">
    <div class="upload-update-view">
      <div class="title-panel">
        <h2>
          {{
            t('uploadUpdateView.updateUpload', {
              referenceNumber: uploadUpdateDependencies?.uploadReferenceNumber,
            })
          }}
        </h2>
        <h1>{{ project?.title }}</h1>
      </div>
      <upload-frames
        :are-no-metadata-file-inputs-disabled="isFileInputDisabled"
        is-upload-update-mode
        :upload-progress="uploadProgress"
        :project-id="props.projectId"
        @mass-upload-records="handleMassUploadRecords"
      />
      <div class="validation-messages">
        <message-wrapper
          v-if="recordsWithMissingRenditionFiles.length"
          severity="error"
        >
          {{
            t('documentUpload.errors.renditionRecordMissing', {
              documentIds: recordsWithMissingRenditionFiles.join(', '),
            })
          }}
        </message-wrapper>
      </div>
      <mass-upload-table
        v-if="shouldShowTable"
        :file-upload-statuses="fileUploadStatuses"
        :metadata-changes="{}"
        :table-busy="false"
        :upload-records="uploadRecords"
      />
      <div class="upload-button-container">
        <vf-button
          v-if="shouldShowTable"
          :disabled="isUploading"
          :size="buttonSizes.large"
          :variant="buttonVariants.primary"
          @click="upload"
        >
          {{ $t('documentUpload.uploadFiles') }}
        </vf-button>
      </div>
      <invalid-update-files-modal
        ref="modalRef"
        :invalid-upload-records="invalidUploadRecords"
      />
    </div>
  </loading-wrapper>
</template>

<script lang="ts" setup>
  import InvalidUpdateFilesModal from '@/components/InvalidUpdateFilesModal/InvalidUpdateFilesModal.vue';
  import { IUploadRecord } from '@/components/MassUpload/MassUpload.types';
  import { useGetDigitalizationProjectData } from '@/composables/api/projects/useGetDigitalizationProjectData';
  import { useGetUploadUpdateDependencies } from '@/composables/api/uploads/useGetUploadUpdateDependencies';
  import { useNavigator } from '@/composables/useNavigator';
  import { useToast } from '@/composables/useToast';
  import { ProjectType } from 'types';
  import { computed, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { DigitalizationProjectDetailsTab } from '../DigitalizationProjectDetails/DigitalizationProjectDetails.types';
  import { ProjectDetailsTab } from '../ProjectDetails/ProjectDetails.types';
  import {
    useUploadUpdate,
    useUploadUpdateRecordsHandler,
  } from './composables';

  const props = defineProps<{
    projectId: string;
    uploadId: string;
  }>();

  const { t } = useI18n();
  const { createSuccessToast, createErrorToast } = useToast();
  const { goToProjectDetailsTab } = useNavigator();

  const uploadRecords = ref<IUploadRecord[]>([]);
  const isUploading = ref(false);

  const { project } = useGetDigitalizationProjectData(props.projectId);
  const { uploadUpdateDependencies } = useGetUploadUpdateDependencies(
    props.uploadId,
  );
  const { invalidUploadRecords, recordsWithMissingRenditionFiles } =
    useUploadUpdateRecordsHandler(uploadRecords, uploadUpdateDependencies);
  const { fileUploadStatuses, uploadProgress, uploadFilesAndFinilize } =
    useUploadUpdate(uploadRecords, uploadUpdateDependencies);

  const isLoading = computed<boolean>(
    () => !uploadUpdateDependencies.value || !project.value,
  );

  const isFileInputDisabled = computed<boolean>(
    () => !!uploadRecords.value.length || !!invalidUploadRecords.value.length,
  );

  const shouldShowTable = computed<boolean>(
    () =>
      !!uploadRecords.value.length &&
      !recordsWithMissingRenditionFiles.value.length,
  );

  function handleMassUploadRecords(records: IUploadRecord[]) {
    uploadRecords.value = records;
  }

  async function upload() {
    try {
      isUploading.value = true;
      await uploadFilesAndFinilize();
      createSuccessToast(t('uploadUpdateView.uploadUpdated'));
      goToProjectDetails();
    } catch {
      createErrorToast(t('uploadUpdateView.errors.uploadUpdateFailed'));
    } finally {
      isUploading.value = false;
    }
  }

  const modalRef = ref<InstanceType<typeof InvalidUpdateFilesModal> | null>(
    null,
  );
  watch(invalidUploadRecords, () => {
    if (invalidUploadRecords.value.length) {
      modalRef.value?.show();
    }
  });

  function goToProjectDetails() {
    goToProjectDetailsTab(
      ProjectType.DIGITALIZATION_PROJECT,
      props.projectId,
      ProjectDetailsTab.UPLOADS,
      DigitalizationProjectDetailsTab.UPLOADS,
    );
  }
</script>

<style lang="scss" scoped>
  .upload-update-view {
    .title-panel {
      margin-bottom: 40px;
      h2 {
        font-weight: 500;
        font-size: 36px;
        line-height: 36px;
      }
      h1 {
        font-weight: 700;
        font-size: 50px;
      }
    }
    .validation-messages {
      margin: 1rem 0;
    }
    .upload-button-container {
      margin-top: 2rem;
      display: flex;
      justify-content: flex-end;
    }
  }
</style>
